/**
 * FirmPortal — dashboard at `/firm-portal/:slug` for verified firm owners.
 *
 * Three tabs:
 *   1. Profile — edit blurb / logo / CTA / contact_email / D&I stats.
 *      Each field submission goes to `firm_edits` for admin review;
 *      once approved it renders on the public firm page with a
 *      `FIRM-SUBMITTED` chip.
 *   2. Bands — Tier-2 only. Submit firm-published salary bands for
 *      admin review; approved bands render on the firm page with an
 *      "Officially supplied by {firm}" chip.
 *   3. Upgrade — if Tier-1, show the tier ladder + Stripe checkout.
 *
 * Also renders an index route at `/firm-portal` (no slug) that lists
 * all firms the current user owns.
 */
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCmsRoles } from "@/hooks/useCmsRoles";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { CheckCircle2, ExternalLink, Sparkles, ShieldCheck, Plus, Trash2, ArrowRight, Info, CalendarDays, Eye, MousePointerClick, LineChart, Flame, TrendingUp } from "lucide-react";
import EventCard from "@/components/app/EventCard";
import CityAutocomplete from "@/components/app/CityAutocomplete";
import TalentSearchTab from "@/components/firm-portal/TalentSearchTab";
import DeadlinesTab from "@/components/firm-portal/DeadlinesTab";
import AlumniMentorsTab from "@/components/firm-portal/AlumniMentorsTab";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip as RechartsTooltip, CartesianGrid } from "recharts";

// -------------------- Index (no slug) --------------------
export function FirmPortalIndex() {
  const { firms, loaded } = useCmsRoles();
  if (!loaded) return null;
  return (
    <main className="max-w-3xl mx-auto px-6 py-10 space-y-6" data-testid="firm-portal-index">
      <h1 className="text-3xl font-editorial">Your firm portal</h1>
      {firms.length === 0 ? (
        <div className="rounded-lg border border-stone-200 bg-stone-50 p-6 text-sm text-stone-600">
          You don't manage any firms yet.{" "}
          <Link to="/firm-guides" className="text-[#4C1D95] font-medium hover:underline">
            Browse firm guides →
          </Link>{" "}
          then click "Claim this profile" on your firm's page.
        </div>
      ) : (
        <ul className="space-y-2" data-testid="firm-portal-list">
          {firms.map((f) => (
            <li key={f.slug}>
              <Link
                to={`/firm-portal/${f.slug}`}
                data-testid={`firm-portal-list-item-${f.slug}`}
                className="flex items-center justify-between rounded-lg border border-stone-200 bg-white px-4 py-3 hover:border-[#4C1D95]/40 transition-colors"
              >
                <div>
                  <div className="font-semibold text-stone-900">{f.name}</div>
                  <div className="text-xs text-stone-500 font-underground-sc tracking-wider mt-0.5">
                    Tier: {f.tier}
                  </div>
                </div>
                <ArrowRight className="h-4 w-4 text-stone-400" />
              </Link>
            </li>
          ))}
        </ul>
      )}
    </main>
  );
}

// -------------------- Portal (slug) --------------------
export default function FirmPortal() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [params] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(() => params.get("tab") || "profile");

  // Feb 2026 — keep tab state in sync with the ?tab= query param so
  // internal deep-links like the "See Verified Partner tiers" CTA
  // actually land on the upgrade tab. Without this, the state was
  // pinned to "profile" and the button felt broken.
  useEffect(() => {
    const q = params.get("tab");
    if (q && q !== tab) setTab(q);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const upgradeState = params.get("upgrade");
  const upgradeSession = params.get("session_id");

  const refetch = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/firm-portal/${slug}`);
      setData(r.data);
    } catch (e) {
      if (e?.response?.status === 403) {
        toast.error("You don't own this firm. Claim it first.");
        navigate(`/claim/${slug}`);
        return;
      }
      toast.error("Could not load firm portal");
    }
    setLoading(false);
  };

  useEffect(() => {
    if (!user) return;
    refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, user]);

  // Poll upgrade status if user just returned from Stripe checkout.
  useEffect(() => {
    if (upgradeState === "success" && upgradeSession) {
      api.get(`/firm-portal/${slug}/upgrade-status/${upgradeSession}`).then((r) => {
        if (r.data?.status === "completed") {
          toast.success("Upgrade complete — you're a Verified Partner!");
          refetch();
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [upgradeState, upgradeSession]);

  if (!user) {
    return (
      <main className="max-w-2xl mx-auto px-6 py-10">
        <p className="text-stone-700">Please sign in to access the Firm Portal.</p>
      </main>
    );
  }
  if (loading || !data) {
    return <main className="max-w-4xl mx-auto px-6 py-10 text-stone-500">Loading…</main>;
  }

  const firm = data.firm;
  const isVerifiedPartner = firm.tier === "verified_partner";

  return (
    <main className="max-w-4xl mx-auto px-6 py-8 space-y-6" data-testid="firm-portal-root">
      <header className="flex items-start justify-between gap-4">
        <div>
          <div className="text-[10px] font-underground-sc tracking-[0.18em] text-stone-500">
            Firm portal
          </div>
          <h1 className="text-3xl font-editorial mt-1">{firm.firm_name}</h1>
          <div className="flex items-center gap-2 mt-2">
            <span
              data-testid="firm-portal-tier-chip"
              className={
                "text-[10px] font-underground-sc tracking-wider rounded-full border px-2 py-0.5 " +
                (isVerifiedPartner
                  ? "border-emerald-400 bg-emerald-50 text-emerald-900"
                  : "border-stone-300 bg-white text-stone-600")
              }
            >
              Tier · {firm.tier}
            </span>
            <Link
              to={`/how-to-get-into/${firm.slug}`}
              className="text-[11px] text-[#4C1D95] hover:underline inline-flex items-center gap-1"
            >
              View public page <ExternalLink className="h-3 w-3" />
            </Link>
          </div>
        </div>
      </header>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="firm-portal-tabs">
          <TabsTrigger value="profile" data-testid="firm-portal-tab-profile">Profile</TabsTrigger>
          <TabsTrigger value="events" data-testid="firm-portal-tab-events">Events</TabsTrigger>
          <TabsTrigger value="bands" data-testid="firm-portal-tab-bands">Salary bands</TabsTrigger>
          <TabsTrigger value="analytics" data-testid="firm-portal-tab-analytics">Analytics</TabsTrigger>
          <TabsTrigger value="talent" data-testid="firm-portal-tab-talent">Talent</TabsTrigger>
          <TabsTrigger value="deadlines" data-testid="firm-portal-tab-deadlines">Deadlines</TabsTrigger>
          <TabsTrigger value="alumni" data-testid="firm-portal-tab-alumni">Alumni</TabsTrigger>
          <TabsTrigger value="upgrade" data-testid="firm-portal-tab-upgrade">
            {isVerifiedPartner ? "Subscription" : "Upgrade"}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="profile" className="pt-6">
          <ProfileTab firm={firm} pending={data.pending_edits} onSubmit={refetch} />
        </TabsContent>
        <TabsContent value="events" className="pt-6">
          <EventsTab firmSlug={firm.slug} tier={firm.tier} />
        </TabsContent>
        <TabsContent value="bands" className="pt-6">
          <BandsTab firm={firm} pending={data.pending_band_submissions} onSubmit={refetch} onGotoUpgrade={() => setTab("upgrade")} />
        </TabsContent>
        <TabsContent value="analytics" className="pt-6">
          <AnalyticsTab firm={firm} onGotoUpgrade={() => setTab("upgrade")} />
        </TabsContent>
        <TabsContent value="talent" className="pt-6">
          <TalentSearchTab firm={firm} onGotoUpgrade={() => setTab("upgrade")} />
        </TabsContent>
        <TabsContent value="deadlines" className="pt-6">
          <DeadlinesTab firm={firm} />
        </TabsContent>
        <TabsContent value="alumni" className="pt-6">
          <AlumniMentorsTab firm={firm} />
        </TabsContent>
        <TabsContent value="upgrade" className="pt-6">
          <UpgradeTab firm={firm} onUpgrade={refetch} />
        </TabsContent>
      </Tabs>
    </main>
  );
}

// -------------------- Profile tab --------------------
function ProfileTab({ firm, pending, onSubmit }) {
  const submitted = firm.firm_submitted || {};
  return (
    <div className="space-y-6" data-testid="firm-portal-profile-tab">
      <IntroCard>
        <p>
          Edit your firm's presentational data. Every field goes through our
          editorial team before publishing — usually within 3-5 working days.
          You <strong>cannot</strong> edit feeder %, band strengths, or cohort
          earnings — those come from graduate destinations data.
        </p>
      </IntroCard>

      {pending.length > 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900" data-testid="firm-portal-pending-edits">
          <div className="font-semibold mb-2 flex items-center gap-1.5">
            <ShieldCheck className="h-4 w-4" /> {pending.length} pending edit{pending.length === 1 ? "" : "s"}
          </div>
          <ul className="space-y-1 text-xs list-disc pl-5">
            {pending.map((e) => (
              <li key={e.id}>
                <span className="font-underground-sc tracking-wider">{e.field}</span>{" "}
                — submitted {new Date(e.created_at).toLocaleDateString("en-GB")}
              </li>
            ))}
          </ul>
        </div>
      )}

      <EditableField
        firmSlug={firm.slug}
        field="blurb"
        label="Firm blurb"
        hint="250 words max. Describes what your firm does + graduate value prop."
        current={submitted.blurb || ""}
        multiline
        onSubmit={onSubmit}
      />
      <EditableField
        firmSlug={firm.slug}
        field="logo_url"
        label="Logo URL (https)"
        hint="Direct link to a transparent PNG or SVG of your logo."
        current={submitted.logo_url || ""}
        placeholder="https://cdn.example.com/logo.png"
        onSubmit={onSubmit}
      />
      <EditableField
        firmSlug={firm.slug}
        field="cta_url"
        label="Careers / apply URL"
        hint="Where should the 'Apply now' button on your firm page go?"
        current={submitted.cta_url || ""}
        placeholder="https://careers.example.com"
        onSubmit={onSubmit}
      />
      <EditableField
        firmSlug={firm.slug}
        field="contact_email"
        label="Candidate-questions inbox"
        hint="Publicly listed on the firm page for applicant enquiries."
        current={submitted.contact_email || ""}
        placeholder="graduates@example.com"
        onSubmit={onSubmit}
      />
    </div>
  );
}

function EditableField({ firmSlug, field, label, hint, current, placeholder, multiline, onSubmit }) {
  const [val, setVal] = useState(current || "");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/firm-portal/${firmSlug}/edits`, { field, value: val });
      toast.success(`${label} submitted for review.`);
      onSubmit();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Could not submit ${field}`);
    }
    setBusy(false);
  };

  const dirty = val.trim() !== (current || "").trim();

  return (
    <div className="rounded-lg border border-stone-200 bg-white p-4 space-y-2" data-testid={`firm-portal-field-${field}`}>
      <div className="flex items-center justify-between">
        <label className="text-sm font-semibold text-stone-900">{label}</label>
        {current && (
          <span className="text-[10px] font-underground-sc tracking-wider text-emerald-700">
            Live on firm page
          </span>
        )}
      </div>
      <p className="text-xs text-stone-500">{hint}</p>
      {multiline ? (
        <Textarea
          data-testid={`firm-portal-field-${field}-input`}
          value={val}
          onChange={(e) => setVal(e.target.value)}
          rows={5}
          placeholder={placeholder}
        />
      ) : (
        <Input
          data-testid={`firm-portal-field-${field}-input`}
          value={val}
          onChange={(e) => setVal(e.target.value)}
          placeholder={placeholder}
        />
      )}
      <Button
        data-testid={`firm-portal-field-${field}-submit`}
        onClick={submit}
        disabled={busy || !dirty || !val.trim()}
        size="sm"
        className="bg-[#4C1D95] text-white hover:bg-[#3B0764]"
      >
        {busy ? "Submitting…" : "Submit for review"}
      </Button>
    </div>
  );
}

// -------------------- Bands tab --------------------
function BandsTab({ firm, pending, onSubmit, onGotoUpgrade }) {
  const isVerifiedPartner = firm.tier === "verified_partner";

  if (!isVerifiedPartner) {
    return (
      <div className="rounded-lg border border-stone-200 bg-stone-50 p-6" data-testid="firm-portal-bands-locked">
        <div className="flex items-center gap-2 text-stone-900 font-semibold">
          <Sparkles className="h-4 w-4 text-amber-600" />
          Firm-submitted bands — Verified Partner only
        </div>
        <p className="text-sm text-stone-600 mt-2">
          Submit your official salary bands (with a source URL and short
          justification). Each submission goes through editorial review, and
          approved bands render on your public page with an "Officially
          supplied by {firm.firm_name}" chip that outranks the amber
          sector-benchmark caveat families see today.
        </p>
        <Button
          data-testid="firm-portal-bands-upgrade-cta"
          className="mt-4 bg-[#4C1D95] text-white hover:bg-[#3B0764]"
          onClick={onGotoUpgrade}
        >
          See Verified Partner tiers <ArrowRight className="h-4 w-4 ml-1.5" />
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="firm-portal-bands-tab">
      <IntroCard>
        <p>
          Submit an official salary band for one of your roles. Every submission
          requires a public source URL (careers page, press release, or offer
          letter with signatures redacted) plus a short justification. Editor
          approval takes 24h for Verified Partners.
        </p>
      </IntroCard>

      {pending.length > 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900" data-testid="firm-portal-pending-bands">
          <div className="font-semibold mb-2">{pending.length} pending band submission{pending.length === 1 ? "" : "s"}</div>
          <ul className="text-xs list-disc pl-5 space-y-1">
            {pending.map((b) => (
              <li key={b.id}>
                {b.role} · {b.currency} {b.base.toLocaleString()} — submitted {new Date(b.created_at).toLocaleDateString("en-GB")}
              </li>
            ))}
          </ul>
        </div>
      )}

      <BandSubmitForm firmSlug={firm.slug} onSubmit={onSubmit} />

      {(firm.firm_submitted_bands || []).length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-stone-900 mb-2">Approved bands (live on firm page)</h3>
          <ul className="space-y-2" data-testid="firm-portal-approved-bands">
            {firm.firm_submitted_bands.map((b) => (
              <li key={b.id} className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm">
                <div className="font-medium text-stone-900">
                  {b.role} · {b.location} · {b.currency} {b.base.toLocaleString()}
                </div>
                <div className="text-xs text-stone-500 mt-0.5">
                  Source: <a href={b.source_url} target="_blank" rel="noreferrer" className="text-[#4C1D95] hover:underline">{b.source_url}</a>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function BandSubmitForm({ firmSlug, onSubmit }) {
  const [form, setForm] = useState({
    role: "", location: "London", currency: "GBP",
    base: "", total_comp: "", bonus: "",
    source_url: "", justification: "",
  });
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/firm-portal/${firmSlug}/bands`, {
        role: form.role,
        location: form.location,
        currency: form.currency,
        base: parseInt(form.base || 0, 10),
        total_comp: form.total_comp ? parseInt(form.total_comp, 10) : null,
        bonus: form.bonus ? parseInt(form.bonus, 10) : null,
        source_url: form.source_url,
        justification: form.justification,
      });
      toast.success("Band submitted for editorial review.");
      setForm({ role: "", location: "London", currency: "GBP", base: "", total_comp: "", bonus: "", source_url: "", justification: "" });
      onSubmit();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit band");
    }
    setBusy(false);
  };

  return (
    <div className="rounded-lg border border-stone-200 bg-white p-4 space-y-3" data-testid="firm-portal-band-form">
      <h3 className="text-sm font-semibold text-stone-900">Submit a new band</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <FormField label="Role" testId="band-role">
          <Input value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} placeholder="Analyst — Investment Banking" data-testid="band-role-input" />
        </FormField>
        <FormField label="Location" testId="band-location">
          <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="London" data-testid="band-location-input" />
        </FormField>
        <FormField label="Currency" testId="band-currency">
          <Input value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value.toUpperCase() })} placeholder="GBP" maxLength={4} data-testid="band-currency-input" />
        </FormField>
        <FormField label="Base salary" testId="band-base">
          <Input value={form.base} onChange={(e) => setForm({ ...form, base: e.target.value.replace(/\D/g, "") })} placeholder="65000" data-testid="band-base-input" />
        </FormField>
        <FormField label="Total comp (optional)" testId="band-total-comp">
          <Input value={form.total_comp} onChange={(e) => setForm({ ...form, total_comp: e.target.value.replace(/\D/g, "") })} placeholder="120000" data-testid="band-total-comp-input" />
        </FormField>
        <FormField label="Bonus (optional)" testId="band-bonus">
          <Input value={form.bonus} onChange={(e) => setForm({ ...form, bonus: e.target.value.replace(/\D/g, "") })} placeholder="30000" data-testid="band-bonus-input" />
        </FormField>
      </div>
      <FormField label="Source URL (mandatory)" hint="Careers page, press release, or offer letter (signatures redacted)." testId="band-source">
        <Input value={form.source_url} onChange={(e) => setForm({ ...form, source_url: e.target.value })} placeholder="https://careers.example.com/analyst-pay-2026" data-testid="band-source-input" />
      </FormField>
      <FormField label="Justification (20-600 chars)" hint="Why should families trust this band? Anything material we should know?" testId="band-justification">
        <Textarea value={form.justification} onChange={(e) => setForm({ ...form, justification: e.target.value })} rows={3} data-testid="band-justification-input" placeholder="This is the 2026 spring intake band published on 15 Jan 2026 and rolled out across all London analyst hires…" />
      </FormField>
      <Button
        data-testid="firm-portal-band-submit-btn"
        onClick={submit}
        disabled={busy || !form.role || !form.base || !form.source_url || form.justification.length < 20}
        className="bg-[#4C1D95] text-white hover:bg-[#3B0764]"
      >
        {busy ? "Submitting…" : "Submit for review"}
      </Button>
    </div>
  );
}

function FormField({ label, hint, children, testId }) {
  return (
    <div className="space-y-1.5" data-testid={testId}>
      <label className="text-xs font-medium text-stone-700">{label}</label>
      {children}
      {hint && <p className="text-[11px] text-stone-500">{hint}</p>}
    </div>
  );
}

// -------------------- Upgrade tab --------------------
const TIERS = [
  { key: "small",  label: "Small firm",  desc: "Up to 250 employees",       priceGBP: 3000 },
  { key: "medium", label: "Mid-size",    desc: "251-2,500 employees",       priceGBP: 5000 },
  { key: "large",  label: "Large firm",  desc: "2,500+ employees",          priceGBP: 10000 },
];

function UpgradeTab({ firm, onUpgrade }) {
  const [busy, setBusy] = useState(false);

  const goToStripe = async (tierKey) => {
    setBusy(true);
    try {
      const r = await api.post(`/firm-portal/${firm.slug}/upgrade`, {
        tier_size: tierKey,
        origin_url: window.location.origin,
      });
      window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create checkout session");
      setBusy(false);
    }
  };

  if (firm.tier === "verified_partner") {
    return (
      <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-6 text-emerald-900" data-testid="firm-portal-already-verified">
        <div className="flex items-center gap-2 text-lg font-semibold">
          <CheckCircle2 className="h-5 w-5" /> You're a Verified Partner
        </div>
        <p className="text-sm mt-2">
          Manage your subscription and view invoices via the billing portal.
          Cancellation takes effect at the end of the current billing period.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="firm-portal-upgrade-tab">
      <IntroCard>
        <p>
          <strong>Verified Partner</strong> unlocks firm-submitted salary bands,
          event promotion (up to 4 concurrent), analytics on your firm page,
          and 24h editorial SLA. Pricing scales by firm size — annual subscription.
        </p>
      </IntroCard>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {TIERS.map((t) => (
          <div
            key={t.key}
            className="rounded-xl border border-stone-200 bg-white p-5 flex flex-col"
            data-testid={`firm-portal-upgrade-tier-${t.key}`}
          >
            <div className="text-[10px] font-underground-sc tracking-[0.18em] text-stone-500">
              {t.desc}
            </div>
            <h3 className="text-lg font-semibold mt-1">{t.label}</h3>
            <div className="text-3xl font-editorial mt-3 text-[#4C1D95]">
              £{t.priceGBP.toLocaleString()}<span className="text-sm text-stone-500">/yr</span>
            </div>
            <ul className="text-xs text-stone-600 mt-3 space-y-1 flex-1">
              <li>✓ Firm-submitted salary bands (beta)</li>
              <li>✓ Up to 4 concurrent events</li>
              <li>✓ 24h editorial SLA</li>
              <li>✓ CTA click-through analytics</li>
              <li>✓ "Officially supplied by" chip</li>
            </ul>
            <Button
              data-testid={`firm-portal-upgrade-btn-${t.key}`}
              onClick={() => goToStripe(t.key)}
              disabled={busy}
              className="mt-4 bg-[#4C1D95] text-white hover:bg-[#3B0764]"
            >
              {busy ? "Redirecting…" : "Upgrade"}
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}

function IntroCard({ children }) {
  return (
    <div className="rounded-lg border border-stone-200 bg-stone-50 p-4 text-sm text-stone-700 flex gap-2 items-start">
      <Info className="h-4 w-4 mt-0.5 text-[#4C1D95] shrink-0" />
      <div>{children}</div>
    </div>
  );
}


// -------------------- Events tab --------------------
const EVENT_TYPES = [
  { key: "insight_week",  label: "Insight Week" },
  { key: "internship",    label: "Internship" },
  { key: "spring_week",   label: "Spring Week" },
  { key: "open_day",      label: "Open Day" },
  { key: "networking",    label: "Networking" },
  { key: "webinar",       label: "Webinar" },
  { key: "conference",    label: "Conference" },
];
const YEAR_GROUPS_LIST = [
  { key: "yr12", label: "Year 12" },
  { key: "yr13", label: "Year 13" },
  { key: "ug1",  label: "UG Year 1" },
  { key: "ug2",  label: "UG Year 2" },
  { key: "ug3",  label: "UG Year 3" },
  { key: "penultimate", label: "Grad Yr 1" },
  { key: "finalist", label: "Grad Yr 2" },
];

function EventsTab({ firmSlug, tier }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  // `editingEvent` holds the doc under edit. When non-null we render
  // the same EventSubmitForm pre-filled in edit mode; on save it
  // PATCHes instead of POSTing.
  const [editingEvent, setEditingEvent] = useState(null);

  const refetch = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/firm-portal/${firmSlug}/events`);
      setData(r.data);
    } catch (e) {
      toast.error("Could not load events");
    }
    setLoading(false);
  };
  useEffect(() => { refetch(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [firmSlug]);

  const cancel = async (eventId) => {
    if (!window.confirm("Cancel this event? Approved events will disappear from public surfaces immediately.")) return;
    try {
      await api.delete(`/firm-portal/${firmSlug}/events/${eventId}`);
      toast.success("Event cancelled");
      refetch();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not cancel");
    }
  };

  const startEdit = (event) => {
    setShowForm(false);
    setEditingEvent(event);
    // Scroll form into view after the state has flushed.
    setTimeout(() => {
      document.querySelector('[data-testid="firm-portal-event-form"]')
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 30);
  };

  if (loading || !data) return <div className="text-stone-500 text-sm">Loading events…</div>;

  const capReached = data.active_count >= data.concurrent_limit && data.concurrent_limit > 0;

  return (
    <div className="space-y-6" data-testid="firm-portal-events-tab">
      <IntroCard>
        <p>
          Publish upcoming events — insight weeks, internships, spring weeks,
          open days. Every submission goes through editorial review.{" "}
          <strong>Tier {tier}</strong> allows up to{" "}
          <strong>{data.concurrent_limit}</strong> concurrent approved event{data.concurrent_limit === 1 ? "" : "s"}.
          You have {data.active_count} active right now.
        </p>
      </IntroCard>

      {data.concurrent_limit === 0 && (
        <div className="rounded-lg border border-stone-200 bg-stone-50 p-4 text-sm text-stone-700" data-testid="firm-portal-events-locked">
          Your tier doesn't support events. Claim your profile first, then upgrade to Verified Partner for up to 4 concurrent events.
        </div>
      )}

      {data.concurrent_limit > 0 && !showForm && !editingEvent && (
        <Button
          data-testid="firm-portal-events-add-btn"
          onClick={() => setShowForm(true)}
          disabled={capReached}
          className="bg-[#4C1D95] text-white hover:bg-[#3B0764]"
        >
          <Plus className="h-4 w-4 mr-1" /> {capReached ? "Concurrent event cap reached" : "Publish a new event"}
        </Button>
      )}

      {(showForm || editingEvent) && (
        <EventSubmitForm
          firmSlug={firmSlug}
          event={editingEvent}
          onDone={() => { setShowForm(false); setEditingEvent(null); refetch(); }}
          onCancel={() => { setShowForm(false); setEditingEvent(null); }}
        />
      )}

      <div className="space-y-3" data-testid="firm-portal-events-list">
        {data.events.length === 0 ? (
          <div className="rounded-lg border border-dashed border-stone-300 bg-stone-50 p-6 text-sm text-stone-500 text-center">
            No events yet. Publish your first one to appear on the /events browse hub.
          </div>
        ) : (
          data.events.map((e) => (
            <EventCard key={e.id} event={e} variant="owner" onCancel={cancel} onEdit={startEdit} showFirm={false} />
          ))
        )}
      </div>
    </div>
  );
}

// Sector labels — kept in sync manually with the same map on
// TalentSearchTab.jsx / TalentProfile.jsx. Event targeting mirrors
// the Firm Talent Search "Sector" filter, so a firm can publish
// "Insight Week for Consulting + Finance students in London" and
// the audience match line lines up 1:1 with their candidate search.
const SECTOR_LABELS = {
  consulting: "Consulting",
  finance: "Finance",
  tech: "Tech",
  law: "Law",
  engineering: "Engineering",
  medicine: "Medicine",
  policy_gov: "Policy / Gov",
  media_creative: "Media / Creative",
  science_research: "Science / Research",
  energy_climate: "Energy / Climate",
  retail_fmcg: "Retail / FMCG",
  aerospace_defence: "Aerospace / Defence",
  startup: "Startup",
  nonprofit: "Nonprofit",
  education: "Education",
};

// Sentinel value that means "event is NOT hosted on a university
// campus" (e.g. firm HQ, hotel, external venue). Backend accepts
// the same literal string via `UNIVERSITY_OUTSIDE` in firm_events.
const UNIVERSITY_OUTSIDE = "outside";

function EventSubmitForm({ firmSlug, event = null, onDone, onCancel }) {
  const isEdit = !!event;
  // Convert an ISO datetime string to the `YYYY-MM-DDTHH:mm` shape
  // the <input type="datetime-local"> control expects. Strips the
  // timezone suffix — the datetime-local input is timezone-naive.
  const _isoToLocalInput = (iso) => {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      const p = (n) => String(n).padStart(2, "0");
      return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
    } catch { return ""; }
  };
  const [form, setForm] = useState(() => {
    if (event) {
      // Hydrate every editable field from the stored event so the
      // recruiter can tweak in place instead of retyping.
      const outsideSentinel = event.university_id === "outside";
      return {
        title: event.title || "",
        event_type: event.event_type || "insight_week",
        starts_at: _isoToLocalInput(event.starts_at),
        ends_at: _isoToLocalInput(event.ends_at),
        city: event.city || "",
        university_id: event.university_id || "",
        // Legacy events (pre-uni-field) stored the venue address in
        // `location`. If Outside is the current pick, surface it in
        // the Venue field so the recruiter can edit it directly.
        venue: outsideSentinel ? (event.location || "") : "",
        is_virtual: !!event.is_virtual,
        target_year_groups: event.target_year_groups || [],
        target_subject_families: event.target_subjects || [],
        target_sectors: event.target_sectors || [],
        rsvp_url: event.rsvp_url || "",
        description: event.description || "",
      };
    }
    return {
      title: "",
      event_type: "insight_week",
      starts_at: "",
      ends_at: "",
      // City replaces the old free-text `location` for uni-hosted
      // events. When university === OUTSIDE we still keep `city` (so
      // the browse hub can group off-campus events by city) plus a
      // dedicated `venue` string for the address line.
      city: "",
      university_id: "",
      venue: "",
      is_virtual: false,
      target_year_groups: [],
      target_subject_families: [],
      target_sectors: [],
      rsvp_url: "",
      description: "",
    };
  });
  const [busy, setBusy] = useState(false);
  // Universities for the current city — refetched each time the
  // user commits a new city string. Empty until a city is typed
  // (or, on edit, seeded from the event's city). "Outside" is
  // appended client-side.
  const [uniOptions, setUniOptions] = useState(() => {
    if (event && event.university_name && event.university_id && event.university_id !== "outside") {
      return [{ id: event.university_id, name: event.university_name }];
    }
    return [];
  });
  const [uniLoading, setUniLoading] = useState(false);
  // Subject-family enum, hydrated once from /api/talent/enums so
  // the labels stay in sync with the Talent tab picker on this
  // same portal.
  const [subjectFamilies, setSubjectFamilies] = useState([]);
  const [allowedSectors, setAllowedSectors] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/talent/enums");
        setSubjectFamilies(data?.subject_families || []);
        setAllowedSectors(data?.allowed_sectors || []);
      } catch { /* silent — pickers just render empty */ }
    })();
  }, []);

  // Refetch university options whenever the city input changes.
  // 250ms debounce so a fast typist doesn't spam the API. When the
  // city is blank we hard-reset options and clear any stale
  // university selection — otherwise the dropdown would still be
  // showing "Imperial College London" when the city says "".
  useEffect(() => {
    const city = (form.city || "").trim();
    if (!city) {
      setUniOptions([]);
      if (form.university_id && form.university_id !== UNIVERSITY_OUTSIDE) {
        setForm((f) => ({ ...f, university_id: "" }));
      }
      return;
    }
    setUniLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/universities", {
          params: { city, limit: 50, view: "card" },
        });
        const opts = (Array.isArray(data) ? data : []).map((u) => ({
          id: u.id, name: u.name,
        }));
        setUniOptions(opts);
        // If the current selection isn't valid for the new city,
        // reset it back to "unselected" so we don't POST a stale id.
        if (form.university_id
            && form.university_id !== UNIVERSITY_OUTSIDE
            && !opts.some((o) => o.id === form.university_id)) {
          setForm((f) => ({ ...f, university_id: "" }));
        }
      } catch {
        setUniOptions([]);
      } finally {
        setUniLoading(false);
      }
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.city]);

  const toggleChip = (field, key) => {
    setForm((f) => ({
      ...f,
      [field]: f[field].includes(key)
        ? f[field].filter((x) => x !== key)
        : [...f[field], key],
    }));
  };

  const submit = async () => {
    setBusy(true);
    try {
      const isOutside = form.university_id === UNIVERSITY_OUTSIDE;
      // location = the free-form venue address when off-campus,
      // otherwise the city (uni-hosted events display "City · Uni").
      const location = isOutside
        ? (form.venue || form.city).trim()
        : (form.city || "").trim();
      const body = {
        title: form.title,
        event_type: form.event_type,
        starts_at: new Date(form.starts_at).toISOString(),
        ends_at: new Date(form.ends_at).toISOString(),
        location,
        is_virtual: form.is_virtual,
        city: form.city,
        university_id: form.university_id,
        target_year_groups: form.target_year_groups,
        // Backend field name stays `target_subjects` for storage
        // continuity; contents are now subject-family keys.
        target_subjects: form.target_subject_families,
        target_sectors: form.target_sectors,
        rsvp_url: form.rsvp_url,
        description: form.description,
      };
      if (isEdit) {
        const { data } = await api.patch(
          `/firm-portal/${firmSlug}/events/${event.id}`,
          body,
        );
        if (data?.rereview_triggered) {
          toast.success(
            "Saved. Re-review triggered — time/place/host changed, event is back in the moderation queue.",
          );
        } else {
          toast.success("Saved — changes are live.");
        }
      } else {
        await api.post(`/firm-portal/${firmSlug}/events`, body);
        toast.success("Event submitted for editorial review.");
      }
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || (isEdit ? "Could not save changes" : "Could not submit event"));
    }
    setBusy(false);
  };

  const isOutside = form.university_id === UNIVERSITY_OUTSIDE;
  const valid = form.title.length >= 6 && form.starts_at && form.ends_at &&
                form.city.trim().length > 0 &&
                form.university_id.length > 0 &&
                (!isOutside || form.venue.trim().length > 0) &&
                form.rsvp_url.startsWith("https://") && form.description.length >= 40;

  return (
    <div className="rounded-lg border border-stone-200 bg-white p-4 space-y-3" data-testid="firm-portal-event-form">
      <h3 className="text-sm font-semibold text-stone-900 flex items-center gap-1.5">
        <CalendarDays className="h-4 w-4 text-[#4C1D95]" />
        {isEdit ? "Edit event" : "Publish a new event"}
        {isEdit && event?.status === "approved" && (
          <span
            className="ml-2 text-[10px] font-normal text-stone-500"
            title="Changing time, city, university or venue puts this event back into moderation."
          >
            · time/place edits will re-trigger review
          </span>
        )}
      </h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <FormField label="Title" testId="event-title">
          <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Insight Week — Global Markets" data-testid="event-title-input" />
        </FormField>
        <FormField label="Type" testId="event-type">
          <select value={form.event_type} onChange={(e) => setForm({ ...form, event_type: e.target.value })}
            className="w-full h-10 rounded-md border border-stone-300 bg-white px-3 text-sm" data-testid="event-type-input">
            {EVENT_TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
          </select>
        </FormField>
        <FormField label="Starts (local time)" testId="event-starts">
          <Input type="datetime-local" value={form.starts_at} onChange={(e) => setForm({ ...form, starts_at: e.target.value })} data-testid="event-starts-input" />
        </FormField>
        <FormField label="Ends (local time)" testId="event-ends">
          <Input type="datetime-local" value={form.ends_at} onChange={(e) => setForm({ ...form, ends_at: e.target.value })} data-testid="event-ends-input" />
        </FormField>
        {/* City-then-University pattern: firm picks the host city
            first, then the uni dropdown filters to that city. Keeps
            the audience match line honest ("London · Imperial") and
            makes the "Outside university" fallback explicit rather
            than smuggled into a free-text location field. */}
        <FormField label="City" testId="event-city"
          hint="Type a UK city. Works with common misspellings — we auto-suggest.">
          <CityAutocomplete
            value={form.city}
            onChange={(v) => setForm({ ...form, city: v })}
            placeholder="e.g. London, Manchester, Edinburgh"
            testIdPrefix="event-city"
          />
        </FormField>
        <FormField label="University" testId="event-university"
          hint="Pick a host university in that city — or Outside university for an event at your own venue.">
          <select
            value={form.university_id}
            onChange={(e) => setForm({ ...form, university_id: e.target.value })}
            className="w-full h-10 rounded-md border border-stone-300 bg-white px-3 text-sm disabled:bg-stone-100 disabled:text-stone-400"
            disabled={!form.city.trim()}
            data-testid="event-university-input"
          >
            <option value="">
              {!form.city.trim()
                ? "Pick a city first"
                : uniLoading
                  ? "Loading universities…"
                  : uniOptions.length === 0
                    ? "No universities matched — pick Outside"
                    : "Select host university…"}
            </option>
            {uniOptions.map((u) => (
              <option key={u.id} value={u.id}>{u.name}</option>
            ))}
            <option value={UNIVERSITY_OUTSIDE}>Outside university (venue below)</option>
          </select>
        </FormField>
        {isOutside && (
          <FormField label="Venue address" testId="event-venue"
            hint="Where exactly is it? e.g. 'Firm HQ, 1 Bishops Square'. Shown on the RSVP page.">
            <Input
              value={form.venue}
              onChange={(e) => setForm({ ...form, venue: e.target.value })}
              placeholder="e.g. 1 Bishops Square, Spitalfields"
              data-testid="event-venue-input"
            />
          </FormField>
        )}
        <FormField label="Virtual?" testId="event-virtual">
          <label className="flex items-center gap-2 text-sm h-10">
            <input type="checkbox" checked={form.is_virtual} onChange={(e) => setForm({ ...form, is_virtual: e.target.checked })} data-testid="event-virtual-input" />
            Runs online only
          </label>
        </FormField>
      </div>
      <FormField label="Target year groups" testId="event-year-groups"
        hint="Pick the audience segments this event is best-suited for.">
        <div className="flex flex-wrap gap-1.5">
          {YEAR_GROUPS_LIST.map((yg) => (
            <button
              key={yg.key}
              type="button"
              data-testid={`event-yg-${yg.key}`}
              onClick={() => toggleChip("target_year_groups", yg.key)}
              className={
                "text-xs px-2.5 py-1 rounded-full border transition-colors " +
                (form.target_year_groups.includes(yg.key)
                  ? "border-[#4C1D95] bg-[#4C1D95] text-white"
                  : "border-stone-300 bg-white text-stone-700 hover:bg-stone-50")
              }
            >
              {yg.label}
            </button>
          ))}
        </div>
      </FormField>
      <FormField label="Target degree families" testId="event-subject-families"
        hint="Same taxonomy the Talent tab uses — pick the broad subject buckets this event targets.">
        <div className="flex flex-wrap gap-1.5">
          {subjectFamilies.length === 0 ? (
            <span className="text-xs text-stone-400">Loading…</span>
          ) : subjectFamilies.map((sf) => (
            <button
              key={sf.key}
              type="button"
              data-testid={`event-sf-${sf.key}`}
              onClick={() => toggleChip("target_subject_families", sf.key)}
              className={
                "text-xs px-2.5 py-1 rounded-full border transition-colors " +
                (form.target_subject_families.includes(sf.key)
                  ? "border-[#4C1D95] bg-[#4C1D95] text-white"
                  : "border-stone-300 bg-white text-stone-700 hover:bg-stone-50")
              }
            >
              {sf.label}
            </button>
          ))}
        </div>
      </FormField>
      <FormField label="Target sectors" testId="event-sectors"
        hint="Which career paths is this event aimed at? Mirrors the Talent search Sector filter.">
        <div className="flex flex-wrap gap-1.5">
          {allowedSectors.length === 0 ? (
            <span className="text-xs text-stone-400">Loading…</span>
          ) : allowedSectors.map((s) => (
            <button
              key={s}
              type="button"
              data-testid={`event-sec-${s}`}
              onClick={() => toggleChip("target_sectors", s)}
              className={
                "text-xs px-2.5 py-1 rounded-full border transition-colors " +
                (form.target_sectors.includes(s)
                  ? "border-[#4C1D95] bg-[#4C1D95] text-white"
                  : "border-stone-300 bg-white text-stone-700 hover:bg-stone-50")
              }
            >
              {SECTOR_LABELS[s] || s}
            </button>
          ))}
        </div>
      </FormField>
      <FormField label="RSVP URL (https)" testId="event-rsvp">
        <Input value={form.rsvp_url} onChange={(e) => setForm({ ...form, rsvp_url: e.target.value })} placeholder="https://apply.example.com/insight-week-2026" data-testid="event-rsvp-input" />
      </FormField>
      <FormField label="Description (40-600 chars)" testId="event-description">
        <Textarea rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="event-description-input" />
        {/* Live counter — turns amber when short (under 40) and
            rose when over cap, so recruiters know exactly why the
            submit button is disabled without having to read
            errors after clicking. */}
        <div
          data-testid="event-description-counter"
          className={
            "text-[11px] mt-1 " +
            (form.description.length < 40 ? "text-amber-600"
              : form.description.length > 600 ? "text-rose-600"
              : "text-stone-400")
          }
        >
          {form.description.length}/600 characters
          {form.description.length < 40
            ? ` — ${40 - form.description.length} more needed`
            : ""}
        </div>
      </FormField>
      {/* Explicit "why is Submit disabled?" hint. Without this
          the greyed-out button leaves recruiters guessing which of
          eight required fields is short — most commonly the 40-char
          description minimum. */}
      {!valid && !busy && (() => {
        const missing = [];
        if (form.title.length < 6) missing.push("title (6+ chars)");
        if (!form.starts_at) missing.push("start time");
        if (!form.ends_at) missing.push("end time");
        if (!form.city.trim()) missing.push("city");
        if (!form.university_id) missing.push("university");
        if (isOutside && !form.venue.trim()) missing.push("venue address");
        if (!form.rsvp_url.startsWith("https://")) missing.push("https RSVP URL");
        if (form.description.length < 40) missing.push(`description (${40 - form.description.length} more chars)`);
        return (
          <div
            data-testid="firm-portal-event-form-missing"
            className="text-[12px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-1.5"
          >
            Needs: {missing.join(" · ")}
          </div>
        );
      })()}
      <div className="flex gap-2">
        <Button data-testid="firm-portal-event-submit-btn" onClick={submit} disabled={busy || !valid} className="bg-[#4C1D95] text-white hover:bg-[#3B0764]">
          {busy ? (isEdit ? "Saving…" : "Submitting…") : (isEdit ? "Save changes" : "Submit for review")}
        </Button>
        <Button data-testid="firm-portal-event-cancel-btn" onClick={onCancel} variant="outline">Cancel</Button>
      </div>
    </div>
  );
}


// -------------------- Analytics tab --------------------
const ANALYTICS_WINDOWS = [
  { key: 7,  label: "Last 7 days" },
  { key: 30, label: "Last 30 days" },
  { key: 90, label: "Last 90 days" },
];

function AnalyticsTab({ firm, onGotoUpgrade }) {
  const isVerifiedPartner = firm.tier === "verified_partner";
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!isVerifiedPartner) return;
    setLoading(true);
    setErr(null);
    api.get(`/firm-portal/${firm.slug}/analytics?days=${days}`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "Could not load analytics"))
      .finally(() => setLoading(false));
  }, [firm.slug, days, isVerifiedPartner]);

  if (!isVerifiedPartner) {
    return (
      <div className="rounded-lg border border-stone-200 bg-stone-50 p-6" data-testid="firm-portal-analytics-locked">
        <div className="flex items-center gap-2 text-stone-900 font-semibold">
          <TrendingUp className="h-4 w-4 text-amber-600" />
          Analytics — Verified Partner only
        </div>
        <p className="text-sm text-stone-600 mt-2">
          Track how families are discovering {firm.firm_name}: firm-page views,
          RSVP click-throughs on your events, careers-page CTA clicks, plus a
          subject heat-map showing which degree pages are driving the traffic.
        </p>
        <Button
          data-testid="firm-portal-analytics-upgrade-cta"
          className="mt-4 bg-[#4C1D95] text-white hover:bg-[#3B0764]"
          onClick={onGotoUpgrade}
        >
          See Verified Partner tiers <ArrowRight className="h-4 w-4 ml-1.5" />
        </Button>
      </div>
    );
  }

  if (loading || !data) return <div className="text-stone-500 text-sm">Loading analytics…</div>;
  if (err) return <div className="text-red-700 text-sm">Error: {err}</div>;

  const kpis = data.kpis || {};
  const totalViews = (kpis.firm_page_view || 0) + (kpis.firm_employer_page_view || 0);

  return (
    <div className="space-y-6" data-testid="firm-portal-analytics-tab">
      <div className="flex items-center justify-between gap-2">
        <IntroCard>
          <p>
            All numbers cover approved, non-bot traffic only. Data is
            anonymous — we don't record IP addresses, just aggregate counts.
          </p>
        </IntroCard>
        <div className="flex gap-1">
          {ANALYTICS_WINDOWS.map((w) => (
            <button
              key={w.key}
              data-testid={`firm-portal-analytics-window-${w.key}`}
              onClick={() => setDays(w.key)}
              className={
                "text-xs px-3 py-1.5 rounded-md border transition-colors " +
                (days === w.key
                  ? "border-[#4C1D95] bg-[#4C1D95] text-white font-semibold"
                  : "border-stone-300 bg-white text-stone-700 hover:bg-stone-50")
              }
            >
              {w.label}
            </button>
          ))}
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="firm-portal-analytics-kpis">
        <KpiCard icon={Eye} label="Firm-page views" value={kpis.firm_page_view || 0} testId="firm-portal-kpi-page-views" tone="violet" />
        <KpiCard icon={Eye} label="Employer-page views" value={kpis.firm_employer_page_view || 0} testId="firm-portal-kpi-employer-page-views" tone="stone" />
        <KpiCard icon={MousePointerClick} label="Event RSVP clicks" value={kpis.firm_rsvp_click || 0} testId="firm-portal-kpi-rsvp-clicks" tone="emerald" />
        <KpiCard icon={MousePointerClick} label="Careers CTA clicks" value={kpis.firm_cta_click || 0} testId="firm-portal-kpi-cta-clicks" tone="amber" />
      </div>

      {/* Daily views chart */}
      <section className="rounded-xl border border-stone-200 bg-white p-4" data-testid="firm-portal-analytics-daily-chart">
        <div className="flex items-center gap-2 mb-3">
          <LineChart className="h-4 w-4 text-[#4C1D95]" />
          <h3 className="text-sm font-semibold text-stone-900">Daily views ({days}d)</h3>
          <span className="ml-auto text-xs text-stone-500 font-mono">{totalViews} total</span>
        </div>
        <div className="h-56 -mx-2" style={{ minWidth: 0, minHeight: 200 }}>
          <ResponsiveContainer width="100%" height="100%" minHeight={200}>
            <BarChart data={data.daily_views} margin={{ top: 8, right: 8, left: 0, bottom: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" vertical={false} />
              <XAxis
                dataKey="day"
                tick={{ fontSize: 10, fill: "#78716c" }}
                tickFormatter={(d) => d?.slice(5)}
                interval={days > 30 ? 6 : Math.max(0, Math.floor(days / 15))}
              />
              <YAxis
                tick={{ fontSize: 10, fill: "#78716c" }}
                allowDecimals={false}
                width={28}
              />
              <RechartsTooltip
                cursor={{ fill: "#F5F0FF" }}
                contentStyle={{ fontSize: 12, borderRadius: 6 }}
                formatter={(v) => [`${v} views`, "Daily"]}
                labelFormatter={(d) => d}
              />
              <Bar dataKey="views" fill="#4C1D95" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Top RSVP'd events */}
        <section className="rounded-xl border border-stone-200 bg-white p-4" data-testid="firm-portal-analytics-top-events">
          <div className="flex items-center gap-2 mb-3">
            <MousePointerClick className="h-4 w-4 text-emerald-600" />
            <h3 className="text-sm font-semibold text-stone-900">Top events by RSVP clicks</h3>
          </div>
          {data.top_events.length === 0 ? (
            <div className="text-xs text-stone-500 italic">No RSVP clicks yet in this window.</div>
          ) : (
            <ul className="space-y-2" data-testid="firm-portal-analytics-top-events-list">
              {data.top_events.map((e, i) => (
                <li key={e.event_id} className="flex items-center gap-2 text-sm">
                  <span className="font-mono text-xs text-stone-400 w-5">{i + 1}.</span>
                  <span className="flex-1 truncate text-stone-900">{e.title}</span>
                  <span className="font-mono text-xs text-emerald-700 font-semibold">{e.clicks}</span>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* Subject heatmap */}
        <section className="rounded-xl border border-stone-200 bg-white p-4" data-testid="firm-portal-analytics-subject-heatmap">
          <div className="flex items-center gap-2 mb-3">
            <Flame className="h-4 w-4 text-orange-500" />
            <h3 className="text-sm font-semibold text-stone-900">Subject heat-map</h3>
            <span className="ml-auto text-[10px] font-underground-sc tracking-wider text-stone-400">
              Which degree pages drove traffic
            </span>
          </div>
          {data.subject_heatmap.length === 0 ? (
            <div className="text-xs text-stone-500 italic">
              No degree-page referrers yet — students haven't clicked through from a degree page in this window.
            </div>
          ) : (
            <ul className="space-y-1" data-testid="firm-portal-analytics-subject-heatmap-list">
              {data.subject_heatmap.map((row) => (
                <SubjectHeatRow key={row.subject} subject={row.subject} views={row.views} max={data.subject_heatmap[0].views} />
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, testId, tone }) {
  const toneMap = {
    violet: "border-[#4C1D95]/25 bg-[#F5F0FF] text-[#4C1D95]",
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-800",
    amber: "border-amber-200 bg-amber-50 text-amber-900",
    stone: "border-stone-200 bg-stone-50 text-stone-700",
  };
  return (
    <div className={`rounded-xl border p-3 ${toneMap[tone] || toneMap.stone}`} data-testid={testId}>
      <div className="flex items-center gap-1.5 text-[10px] font-underground-sc tracking-wider opacity-80">
        <Icon className="h-3 w-3" /> {label}
      </div>
      <div className="mt-1.5 font-editorial text-2xl tabular-nums">
        {value.toLocaleString()}
      </div>
    </div>
  );
}

function SubjectHeatRow({ subject, views, max }) {
  const pct = max > 0 ? Math.max(4, (views / max) * 100) : 4;
  return (
    <li className="flex items-center gap-2 text-xs">
      <span className="w-32 truncate text-stone-700">{subject}</span>
      <div className="flex-1 h-2 bg-stone-100 rounded overflow-hidden">
        <div className="h-full bg-orange-400 rounded" style={{ width: `${pct}%` }} />
      </div>
      <span className="font-mono text-stone-600 w-8 text-right">{views}</span>
    </li>
  );
}
